import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Sparkles, 
  Copy, 
  Eye, 
  EyeOff, 
  Archive,
  Users,
  Settings,
  Plus,
  Loader2,
  Check,
  X,
  AlertCircle,
  Trash2,
  ChevronDown
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import api from '../../lib/api';

const AdminSpecialPrograms = () => {
  const [groupedPrograms, setGroupedPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedBlocks, setExpandedBlocks] = useState({});
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [showNewTypeModal, setShowNewTypeModal] = useState(false);
  const [cloneSource, setCloneSource] = useState(null);
  const [cloning, setCloning] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [cloneForm, setCloneForm] = useState({
    title: '',
    slug: '',
    clone_type: 'new_edition',
    landing_public: true,
    migrate_enrollments: false,
    migration_mode: 'copy'
  });
  const [newTypeForm, setNewTypeForm] = useState({
    course_type: '',
    course_type_name: '',
    title: '',
    slug: ''
  });

  useEffect(() => {
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    try {
      const response = await api.get('/admin/special-programs/grouped');
      setGroupedPrograms(response.data);
      // Expand all blocks by default
      const expanded = {};
      response.data.forEach(group => {
        expanded[group.course_type] = true;
      });
      setExpandedBlocks(expanded);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar los programas');
    } finally {
      setLoading(false);
    }
  };

  const toggleBlock = (courseType) => {
    setExpandedBlocks(prev => ({
      ...prev,
      [courseType]: !prev[courseType]
    }));
  };

  const openCloneModal = (program, isNewEdition = true) => {
    setCloneSource(program);
    const baseSlug = program.slug.replace(/-\d{4}$/, '');
    const newYear = new Date().getFullYear() + 1;
    setCloneForm({
      title: `${program.course_type_name || program.title} (${newYear})`,
      slug: `${baseSlug}-${newYear}`,
      clone_type: 'new_edition',
      landing_public: true,
      migrate_enrollments: false,
      migration_mode: 'copy'
    });
    setShowCloneModal(true);
  };

  const handleClone = async () => {
    if (!cloneForm.title.trim() || !cloneForm.slug.trim()) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    setCloning(true);
    try {
      const response = await api.post(`/admin/special-programs/${cloneSource.slug}/clone`, cloneForm);
      toast.success(`Programa clonado correctamente. ${response.data.enrollments_migrated > 0 ? `${response.data.enrollments_migrated} inscripciones migradas.` : ''}`);
      setShowCloneModal(false);
      fetchPrograms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al clonar el programa');
    } finally {
      setCloning(false);
    }
  };

  const updateVisibility = async (slug, field, value) => {
    try {
      await api.put(`/admin/special-courses/${slug}/visibility`, { [field]: value });
      // Update grouped programs
      setGroupedPrograms(prev => prev.map(group => ({
        ...group,
        editions: group.editions.map(p => 
          p.slug === slug ? { ...p, [field]: value } : p
        )
      })));
      toast.success('Actualizado correctamente');
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const closeProgram = async (slug) => {
    if (!window.confirm('¿Cerrar esta edición? Las alumnas inscritas mantienen acceso completo.')) {
      return;
    }
    try {
      await api.delete(`/admin/special-programs/${slug}`);
      fetchPrograms();
      toast.success('Edición cerrada. Las alumnas mantienen acceso.');
    } catch (error) {
      toast.error('Error al cerrar');
    }
  };

  const deleteProgram = async (slug, enrollments) => {
    if (enrollments > 0) {
      toast.error(`No se puede eliminar: hay ${enrollments} inscripciones activas`);
      return;
    }
    if (!window.confirm('¿Eliminar esta edición permanentemente? Esta acción no se puede deshacer.')) {
      return;
    }
    setDeleting(slug);
    try {
      await api.delete(`/admin/special-programs/${slug}/permanent`);
      fetchPrograms();
      toast.success('Edición eliminada permanentemente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    } finally {
      setDeleting(null);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      open: { bg: 'bg-green-100', text: 'text-green-700', label: '🟢 Venta abierta' },
      closed: { bg: 'bg-amber-100', text: 'text-amber-700', label: '🔒 Cerrada (archivo)' },
      draft: { bg: 'bg-gray-100', text: 'text-gray-600', label: '📝 En preparación' }
    };
    const badge = badges[status] || badges.draft;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--accent))]" />
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-[hsl(var(--sage-light))]">
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link 
              to="/admin" 
              className="inline-flex items-center gap-2 text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--text-primary))] mb-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
              Volver al panel
            </Link>
            <h1 className="text-2xl font-heading text-[hsl(var(--text-primary))]">
              Formaciones Especiales
            </h1>
            <p className="text-sm text-[hsl(var(--text-secondary))] mt-1">
              Gestiona todos los tipos de formación y sus ediciones
            </p>
          </div>
          <Button
            onClick={() => setShowNewTypeModal(true)}
            className="btn-gold"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nueva formación
          </Button>
        </div>

        {/* Grouped Programs - Blocks */}
        <div className="space-y-6">
          {groupedPrograms.map((group) => (
            <div 
              key={group.course_type}
              className="bg-white rounded-2xl shadow-sm border border-[hsl(var(--border))] overflow-hidden"
            >
              {/* Block Header */}
              <div 
                className="p-5 bg-gradient-to-r from-[hsl(var(--accent))]/5 to-transparent cursor-pointer hover:bg-[hsl(var(--accent))]/10 transition-colors"
                onClick={() => toggleBlock(group.course_type)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[hsl(var(--accent))]/20 to-[hsl(var(--sage))]/20 flex items-center justify-center">
                      <Sparkles className="w-6 h-6 text-[hsl(var(--accent))]" />
                    </div>
                    <div>
                      <h2 className="font-heading text-xl text-[hsl(var(--text-primary))]">
                        {group.course_type_name}
                      </h2>
                      <div className="flex items-center gap-3 mt-1 text-sm text-[hsl(var(--text-tertiary))]">
                        <span>{group.editions.length} edicion{group.editions.length !== 1 ? 'es' : ''}</span>
                        <span>•</span>
                        <span className="text-green-600">{group.active_editions} activa{group.active_editions !== 1 ? 's' : ''}</span>
                        <span>•</span>
                        <span><Users className="w-3 h-3 inline mr-1" />{group.total_enrollments} inscritos total</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        openCloneModal(group.editions[0]);
                      }}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Nueva edición
                    </Button>
                    <ChevronDown className={`w-5 h-5 text-[hsl(var(--text-tertiary))] transition-transform ${expandedBlocks[group.course_type] ? 'rotate-180' : ''}`} />
                  </div>
                </div>
              </div>

              {/* Editions List */}
              {expandedBlocks[group.course_type] && (
                <div className="border-t border-[hsl(var(--border))]">
                  {group.editions.map((program, idx) => (
                    <div 
                      key={program.id}
                      className={`p-5 ${idx !== group.editions.length - 1 ? 'border-b border-[hsl(var(--border))]' : ''} ${program.status === 'archived' ? 'opacity-60 bg-gray-50' : ''}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-lg bg-[hsl(var(--sage-light))] flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-xs font-medium text-[hsl(var(--text-tertiary))]">
                              {idx + 1}
                            </span>
                          </div>
                          <div>
                            <h3 className="font-medium text-[hsl(var(--text-primary))]">
                              {program.title}
                            </h3>
                            <p className="text-xs text-[hsl(var(--text-tertiary))] mt-0.5">
                              /{program.slug}
                            </p>
                            <div className="flex flex-wrap items-center gap-2 mt-2">
                              {getStatusBadge(program.status)}
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                program.landing_public 
                                  ? 'bg-blue-100 text-blue-700' 
                                  : 'bg-orange-100 text-orange-700'
                              }`}>
                                {program.landing_public ? '👁 Pública' : '🔒 Oculta'}
                              </span>
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-[hsl(var(--sage-light))] text-[hsl(var(--text-secondary))]">
                                <Users className="w-3 h-3 inline mr-1" />
                                {program.active_enrollments} inscritos
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-wrap items-center gap-2">
                          <select
                            value={program.status}
                            onChange={(e) => updateVisibility(program.slug, 'status', e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="px-2 py-1 rounded-lg bg-white border border-[hsl(var(--border))] text-xs focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent))]"
                          >
                            <option value="draft">📝 En preparación</option>
                            <option value="open">🟢 Venta abierta</option>
                            <option value="closed">🔒 Cerrada</option>
                            <option value="archived">📦 Archivada</option>
                          </select>
                          
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => updateVisibility(program.slug, 'landing_public', !program.landing_public)}
                            className={`h-7 ${program.landing_public ? '' : 'border-orange-300 text-orange-600'}`}
                          >
                            {program.landing_public ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                          </Button>
                        </div>
                      </div>

                      {/* Quick Links */}
                      <div className="mt-3 pt-3 border-t border-[hsl(var(--border))]/50 flex flex-wrap gap-2 ml-11">
                        <Link to={`/admin/formaciones/${program.slug}/editar`}>
                          <Button variant="outline" size="sm" className="h-7 text-xs">
                            Editar Landing
                          </Button>
                        </Link>
                        <Link to={`/admin/formaciones/${program.slug}/modulos`}>
                          <Button variant="outline" size="sm" className="h-7 text-xs">
                            Gestionar Módulos
                          </Button>
                        </Link>
                        <Link to={`/admin/formaciones/${program.slug}/inscripciones`}>
                          <Button variant="outline" size="sm" className="h-7 text-xs">
                            Ver Inscripciones
                          </Button>
                        </Link>
                        <a 
                          href={`/formaciones/${program.slug}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          <Button variant="outline" size="sm" className="h-7 text-xs">
                            <Eye className="w-3 h-3 mr-1" />
                            Ver Landing
                          </Button>
                        </a>
                        {program.active_enrollments === 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deleteProgram(program.slug, program.active_enrollments)}
                            disabled={deleting === program.slug}
                            className="h-7 text-xs text-red-600 hover:text-red-700 border-red-300 hover:bg-red-50"
                          >
                            {deleting === program.slug ? (
                              <Loader2 className="w-3 h-3 animate-spin" /> ) : (
                              <>
                                <Trash2 className="w-3 h-3 mr-1" />
                                Eliminar
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {groupedPrograms.length === 0 && (
          <div className="card-soft p-12 text-center">
            <Sparkles className="w-12 h-12 mx-auto mb-4 text-[hsl(var(--accent))]/30" />
            <p className="text-[hsl(var(--text-secondary))]">
              No hay formaciones especiales configuradas
            </p>
            <Button
              onClick={() => setShowNewTypeModal(true)}
              className="btn-gold mt-4"
            >
              <Plus className="w-4 h-4 mr-2" />
              Crear primera formación
            </Button>
          </div>
        )}
      </div>

      {/* Clone Modal (Nueva Edición) */}
      {showCloneModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[hsl(var(--border))]">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-xl text-[hsl(var(--text-primary))]">
                  Nueva Edición
                </h2>
                <button onClick={() => setShowCloneModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-[hsl(var(--text-secondary))] mt-1">
                Basada en: <strong>{cloneSource?.title}</strong>
              </p>
            </div>

            <div className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
                  Nombre de la edición
                </label>
                <Input
                  value={cloneForm.title}
                  onChange={(e) => setCloneForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Ej: Facilitadoras 2026"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
                  URL (slug)
                </label>
                <div className="flex items-center">
                  <span className="text-sm text-[hsl(var(--text-tertiary))] mr-1">/formaciones/</span>
                  <Input
                    value={cloneForm.slug}
                    onChange={(e) => setCloneForm(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))}
                    placeholder="facilitadoras-2026"
                    className="flex-1"
                  />
                </div>
              </div>

              <div className="p-4 rounded-xl bg-[hsl(var(--sage-light))] border border-[hsl(var(--sage))]/20">
                <p className="text-sm text-[hsl(var(--text-secondary))]">
                  <strong>La nueva edición incluirá:</strong>
                </p>
                <ul className="text-xs text-[hsl(var(--text-tertiary))] mt-2 space-y-1">
                  <li>✓ Landing page completa</li>
                  <li>✓ Área de estudiantes</li>
                  <li>✓ Configuración de precios</li>
                </ul>
                <p className="text-xs text-[hsl(var(--text-tertiary))] mt-2 italic">
                  Empezará en estado "En preparación" (draft)
                </p>
              </div>

              <div className="flex items-center justify-between p-4 rounded-xl bg-[hsl(var(--sage-light))]">
                <div>
                  <p className="font-medium text-[hsl(var(--text-primary))]">Landing pública</p>
                  <p className="text-xs text-[hsl(var(--text-tertiary))]">Puedes cambiarlo después</p>
                </div>
                <button
                  onClick={() => setCloneForm(prev => ({ ...prev, landing_public: !prev.landing_public }))}
                  className={`w-12 h-7 rounded-full transition-colors relative ${
                    cloneForm.landing_public ? 'bg-[hsl(var(--accent))]' : 'bg-gray-300'
                  }`}
                >
                  <span className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ${
                    cloneForm.landing_public ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
            </div>

            <div className="p-6 border-t border-[hsl(var(--border))] flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowCloneModal(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleClone}
                disabled={cloning}
                className="flex-1 btn-gold"
              >
                {cloning ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Creando...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Crear edición
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* New Course Type Modal */}
      {showNewTypeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[hsl(var(--border))]">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-xl text-[hsl(var(--text-primary))]">
                  Nueva Formación
                </h2>
                <button onClick={() => setShowNewTypeModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-[hsl(var(--text-secondary))] mt-1">
                Crea un nuevo tipo de formación especial
              </p>
            </div>

            <div className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
                  Nombre de la formación
                </label>
                <Input
                  value={newTypeForm.course_type_name}
                  onChange={(e) => setNewTypeForm(prev => ({ 
                    ...prev, 
                    course_type_name: e.target.value,
                    course_type: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-'),
                    title: e.target.value,
                    slug: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-')
                  }))}
                  placeholder="Ej: LÍMITES, Viajes Iniciáticos..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
                  URL (slug)
                </label>
                <div className="flex items-center">
                  <span className="text-sm text-[hsl(var(--text-tertiary))] mr-1">/formaciones/</span>
                  <Input
                    value={newTypeForm.slug}
                    onChange={(e) => setNewTypeForm(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))}
                    placeholder="limites"
                    className="flex-1"
                  />
                </div>
              </div>

              <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
                <p className="text-sm text-amber-800">
                  <strong>ℹ️ Nota:</strong> Se creará una formación vacía. Tendrás que configurar la landing y el contenido desde el panel de administración.
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-[hsl(var(--border))] flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowNewTypeModal(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={async () => {
                  if (!newTypeForm.course_type_name || !newTypeForm.slug) {
                    toast.error('Por favor completa todos los campos');
                    return;
                  }
                  try {
                    await api.post('/admin/special-programs/create', {
                      title: newTypeForm.course_type_name,
                      slug: newTypeForm.slug,
                      course_type: newTypeForm.course_type,
                      course_type_name: newTypeForm.course_type_name
                    });
                    toast.success('Formación creada correctamente');
                    setShowNewTypeModal(false);
                    setNewTypeForm({ course_type: '', course_type_name: '', title: '', slug: '' });
                    fetchPrograms();
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Error al crear la formación');
                  }
                }}
                className="flex-1 btn-gold"
              >
                <Plus className="w-4 h-4 mr-2" />
                Crear formación
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSpecialPrograms;
